import React, { useState, useEffect, useCallback } from 'react';
import { Users, FileText, CheckCircle, XCircle, Settings, UserX, Edit3, Database, Plus, Trash2, Calendar, Clock, Mail, Target, ChevronDown, ChevronRight, AlertTriangle, Star, Building2, UserPlus, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, publicationAPI, dataSourcesAPI, meetingAPI, timelineAPI, objectivesAPI, teamAPI } from '../services/api';
import api from '../services/api';
import { User, Publication, DataSource, Team } from '../types';
import EditPublicationModal from '../components/Publications/EditPublicationModal';
import DataSourceModal from '../components/Admin/DataSourceModal';
import FilePreviewModal from '../components/Meetings/FilePreviewModal';
import ResubmitModal from '../components/Publications/ResubmitModal';
import TimelineNotificationModal from '../components/Admin/TimelineNotificationModal';
import TimelineEditModal from '../components/Admin/TimelineEditModal';
import TimelineCalendar from '../components/Admin/TimelineCalendar';
import PriorityModal from '../components/Admin/PriorityModal';
import TeamModal from '../components/Admin/TeamModal';
import TeamMemberModal from '../components/Admin/TeamMemberModal';
import ChangeLeadAuthorModal from '../components/Publications/ChangeLeadAuthorModal';
import toast from 'react-hot-toast';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'publications' | 'users' | 'data-sources' | 'meetings' | 'timeline' | 'objectives' | 'teams' | 'backups'>('publications');
  const [publications, setPublications] = useState<Publication[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [timelines, setTimelines] = useState<any[]>([]);
  const [objectives, setObjectives] = useState<any[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<Publication | null>(null);
  const [isCreateDataSourceModalOpen, setIsCreateDataSourceModalOpen] = useState(false);
  const [isEditDataSourceModalOpen, setIsEditDataSourceModalOpen] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
  const [meetingViewMode, setMeetingViewMode] = useState<'current' | 'archive'>('current');
  const [timelineViewMode, setTimelineViewMode] = useState<'current' | 'archive'>('current');
  const [objectiveViewMode, setObjectiveViewMode] = useState<'current' | 'archive'>('current');
  
  // Pagination states
  const [meetingPage, setMeetingPage] = useState(1);
  const [objectivePage, setObjectivePage] = useState(1);
  const [publicationPage, setPublicationPage] = useState(1);
  const [dataSourcePage, setDataSourcePage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [meetingExpandedItems, setMeetingExpandedItems] = useState<Set<string>>(new Set());
  const [objectiveExpandedItems, setObjectiveExpandedItems] = useState<Set<string>>(new Set());
  
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const [publicationToResubmit, setPublicationToResubmit] = useState<Publication | null>(null);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [isTimelineNotificationModalOpen, setIsTimelineNotificationModalOpen] = useState(false);
  const [selectedTimelineForNotification, setSelectedTimelineForNotification] = useState<any>(null);
  const [isTimelineEditModalOpen, setIsTimelineEditModalOpen] = useState(false);
  const [selectedTimelineForEdit, setSelectedTimelineForEdit] = useState<any>(null);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const [selectedPublicationForPriority, setSelectedPublicationForPriority] = useState<Publication | null>(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isTeamMemberModalOpen, setIsTeamMemberModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isChangeLeadAuthorModalOpen, setIsChangeLeadAuthorModalOpen] = useState(false);
  const [selectedPublicationForLeadAuthor, setSelectedPublicationForLeadAuthor] = useState<Publication | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Use batch endpoint to reduce API calls
      const response = await api.get('/admin/batch');
      const { publications, users, dataSources, teams } = response.data;
      
      setPublications(publications);
      setUsers(users);
      setDataSources(dataSources);
      setTeams(teams);
      
      // Initialize empty arrays for meetings, timelines, and objectives
      // These will be loaded on-demand when the user clicks on specific tabs
      setMeetings([]);
      setTimelines([]);
      setObjectives([]);
    } catch (error: any) {
      toast.error('Failed to load admin data');
      console.error('Error loading admin data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      loadData();
    }
  }, [user, loadData]);

  const loadAllMeetings = useCallback(async () => {
    if (meetings.length > 0 || isLoadingData) return; // Already loaded or currently loading
    
    try {
      setIsLoadingData(true);
      setIsLoading(true);
      const allMeetingsData: any[] = [];
      
      // Load meetings for each publication
      for (const publication of publications) {
        try {
          const meetingsData = await meetingAPI.getMeetings(publication.id);
          // Sort meetings by meeting date first, then add sequence numbers
          const sortedMeetings = meetingsData.sort((a, b) => {
            const dateA = a.meeting_date ? new Date(a.meeting_date).getTime() : new Date(a.created_at).getTime();
            const dateB = b.meeting_date ? new Date(b.meeting_date).getTime() : new Date(b.created_at).getTime();
            return dateB - dateA; // Newest first
          });

          // Add publication info and sequence number to each meeting
          const meetingsWithPublication = sortedMeetings.map((meeting, index) => ({
            ...meeting,
            publication_title: publication.title,
            publication_id: publication.id,
            publication_stage: publication.stage,
            sequence_number: index + 1, // Sequential numbering (newest = 1)
            meeting_date: meeting.meeting_date 
              ? new Date(meeting.meeting_date).toLocaleDateString()
              : new Date(meeting.created_at).toLocaleDateString()
          }));
          allMeetingsData.push(...meetingsWithPublication);
        } catch (error) {
          // Skip publications that don't have meetings or have errors
          console.log(`No meetings found for publication ${publication.id}`);
        }
      }
      
      // Sort meetings by meeting date (newest first), fallback to created_at if meeting_date is null
      allMeetingsData.sort((a, b) => {
        const dateA = a.meeting_date ? new Date(a.meeting_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.meeting_date ? new Date(b.meeting_date).getTime() : new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      setMeetings(allMeetingsData);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  }, [publications, meetings.length, isLoadingData]);

  const loadAllTimelines = useCallback(async () => {
    if (timelines.length > 0 || isLoadingData) return; // Already loaded or currently loading
    
    try {
      setIsLoadingData(true);
      setIsLoading(true);
      const allTimelinesData: any[] = [];
      
      // Load timelines for each publication
      for (const publication of publications) {
        try {
          const timelineData = await timelineAPI.getTimeline(publication.id);
          
          // Add publication info to each timeline item
          const timelinesWithPublication = timelineData.map((timeline: any) => ({
            ...timeline,
            publication_title: publication.title,
            publication_id: publication.id,
            publication_stage: publication.stage
          }));
          allTimelinesData.push(...timelinesWithPublication);
        } catch (error) {
          // Skip publications that don't have timelines or have errors
          console.log(`No timelines found for publication ${publication.id}`);
        }
      }
      
      // Sort timelines by due date (newest first), fallback to created_at if due_date is null
      allTimelinesData.sort((a, b) => {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.due_date ? new Date(b.due_date).getTime() : new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      setTimelines(allTimelinesData);
    } catch (error) {
      console.error('Error loading timelines:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  }, [publications, timelines.length, isLoadingData]);

  // Force reload timelines (bypasses guard clause)
  const forceReloadTimelines = useCallback(async () => {
    try {
      setIsLoadingData(true);
      setIsLoading(true);
      const allTimelinesData: any[] = [];
      
      // Load timelines for each publication
      for (const publication of publications) {
        try {
          const timelineData = await timelineAPI.getTimeline(publication.id);
          
          // Add publication info to each timeline item
          const timelinesWithPublication = timelineData.map((timeline: any) => ({
            ...timeline,
            publication_title: publication.title,
            publication_id: publication.id,
            publication_stage: publication.stage
          }));
          allTimelinesData.push(...timelinesWithPublication);
        } catch (error) {
          // Skip publications that don't have timelines or have errors
          console.log(`No timelines found for publication ${publication.id}`);
        }
      }
      
      // Sort timelines by due date (newest first), fallback to created_at if due_date is null
      allTimelinesData.sort((a, b) => {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.due_date ? new Date(b.due_date).getTime() : new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      setTimelines(allTimelinesData);
    } catch (error) {
      console.error('Error loading timelines:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  }, [publications]);

  const loadAllObjectives = useCallback(async () => {
    if (objectives.length > 0 || isLoadingData) return; // Already loaded or currently loading
    
    try {
      setIsLoadingData(true);
      setIsLoading(true);
      const allObjectivesData: any[] = [];
      
      // Load objectives for each publication
      for (const publication of publications) {
        try {
          const objectivesData = await objectivesAPI.getObjectives(publication.id);
          
          // Add publication info to each objective
          const objectivesWithPublication = objectivesData.map((objective: any) => ({
            ...objective,
            publication_title: publication.title,
            publication_id: publication.id,
            publication_stage: publication.stage
          }));
          allObjectivesData.push(...objectivesWithPublication);
        } catch (error) {
          // Skip publications that don't have objectives or have errors
          console.log(`No objectives found for publication ${publication.id}`);
        }
      }
      
      // Sort objectives by creation date (newest first)
      allObjectivesData.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setObjectives(allObjectivesData);
    } catch (error) {
      console.error('Error loading objectives:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  }, [publications, objectives.length, isLoadingData]);

  const handleApprovePublication = async (id: number) => {
    try {
      await publicationAPI.approvePublication(id, 'Approved by admin');
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern(`/publications/${id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Publication approved successfully');
      loadData();
    } catch (error: any) {
      toast.error('Failed to approve publication');
    }
  };

  const handleRejectPublication = async (id: number) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      try {
        await publicationAPI.rejectPublication(id, reason);
        
        // Clear cache to ensure fresh data
        const { cacheService } = await import('../services/cache');
        cacheService.clearPattern(`/publications/${id}/batch`);
        cacheService.clearPattern('/dashboard/batch');
        cacheService.clearPattern('/admin/batch');
        
        toast.success('Publication rejected');
        loadData();
      } catch (error: any) {
        toast.error('Failed to reject publication');
      }
    }
  };

  const handleResubmitPublication = (publication: Publication) => {
    setPublicationToResubmit(publication);
    setIsResubmitModalOpen(true);
  };

  const handleConfirmResubmit = async (feedback: string) => {
    if (!publicationToResubmit) return;
    
    try {
      setIsResubmitting(true);
      await publicationAPI.resubmitPublication(publicationToResubmit.id, feedback);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern(`/publications/${publicationToResubmit.id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Publication sent back for resubmission');
      loadData();
      setIsResubmitModalOpen(false);
      setPublicationToResubmit(null);
    } catch (error: any) {
      console.error('Resubmit error:', error);
      toast.error('Failed to resubmit publication');
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleChangeStage = async (id: number, stage: '1' | '2' | '3' | '4' | 'published') => {
    try {
      await publicationAPI.updateStage(id, { stage });
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern(`/publications/${id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Stage updated');
      loadData();
    } catch (error: any) {
      toast.error('Failed to update stage');
    }
  };

  const handleUpdateUserRole = async (userId: number, newRole: string) => {
    try {
      await userAPI.updateUserRole(userId, newRole);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('User role updated successfully');
      loadData();
    } catch (error: any) {
      toast.error('Failed to update user role');
    }
  };

  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    const action = isActive ? 'deactivate' : 'activate';
    const confirmMessage = isActive 
      ? 'Are you sure you want to deactivate this user? They will not be able to log in.'
      : 'Are you sure you want to activate this user?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (isActive) {
        await userAPI.deactivateUser(userId);
        toast.success('User deactivated successfully');
      } else {
        await userAPI.activateUser(userId);
        toast.success('User activated successfully');
      }
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      loadData();
    } catch (error: any) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const handleEditPublication = (publication: Publication) => {
    setSelectedPublication(publication);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedPublication(null);
  };

  const handleCreateDataSource = () => {
    setSelectedDataSource(null);
    setIsCreateDataSourceModalOpen(true);
  };

  const handleEditDataSource = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setIsEditDataSourceModalOpen(true);
  };

  const handleDeleteDataSource = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this data source? This action cannot be undone.')) {
      try {
        await dataSourcesAPI.deleteDataSource(id);
        toast.success('Data source deleted successfully');
        loadData();
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to delete data source');
      }
    }
  };

  // Removed unused handleDownloadFile function

  const handlePreviewFile = (meetingId: number, file: any) => {
    setSelectedMeetingId(meetingId);
    setSelectedFile(file);
    setIsFilePreviewModalOpen(true);
  };

  const handleDownloadObjectiveAttachment = async (objectiveId: number, attachmentId: number, filename: string) => {
    try {
      const blob = await objectivesAPI.downloadAttachment(objectiveId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading objective attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const getFilteredMeetings = () => {
    if (meetingViewMode === 'current') {
      return meetings.filter(meeting => meeting.publication_stage !== 'published');
    } else {
      return meetings.filter(meeting => meeting.publication_stage === 'published');
    }
  };

  const getGroupedMeetings = () => {
    const filteredMeetings = getFilteredMeetings();
    const grouped: { [key: string]: any[] } = {};
    
    filteredMeetings.forEach(meeting => {
      const key = `${meeting.publication_id}_${meeting.publication_title}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(meeting);
    });
    
    // Sort meetings within each group by meeting date (newest first), fallback to created_at if meeting_date is null
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const dateA = a.meeting_date ? new Date(a.meeting_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.meeting_date ? new Date(b.meeting_date).getTime() : new Date(b.created_at).getTime();
        return dateB - dateA;
      });
    });
    
    return grouped;
  };

  const getFilteredTimelines = () => {
    if (timelineViewMode === 'current') {
      return timelines.filter(timeline => timeline.publication_stage !== 'published');
    } else {
      return timelines.filter(timeline => timeline.publication_stage === 'published');
    }
  };

  const getGroupedTimelines = () => {
    const filteredTimelines = getFilteredTimelines();
    const grouped: { [key: string]: any[] } = {};
    
    filteredTimelines.forEach(timeline => {
      const key = `${timeline.publication_id}_${timeline.publication_title}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(timeline);
    });
    
    // Sort timelines within each group by due date (newest first), fallback to created_at if due_date is null
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.due_date ? new Date(b.due_date).getTime() : new Date(b.created_at).getTime();
        return dateB - dateA;
      });
    });
    
    return grouped;
  };

  const getFilteredObjectives = () => {
    if (objectiveViewMode === 'current') {
      return objectives.filter(objective => objective.publication_stage !== 'published');
    } else {
      return objectives.filter(objective => objective.publication_stage === 'published');
    }
  };

  const getGroupedObjectives = () => {
    const filteredObjectives = getFilteredObjectives();
    const grouped: { [key: string]: any[] } = {};
    
    filteredObjectives.forEach(objective => {
      const key = `${objective.publication_id}_${objective.publication_title}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(objective);
    });
    
    // Sort objectives within each group by creation date (newest first)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    });
    
    return grouped;
  };

  const handleCloseDataSourceModals = () => {
    setIsCreateDataSourceModalOpen(false);
    setIsEditDataSourceModalOpen(false);
    setSelectedDataSource(null);
  };

  const handleOpenTimelineNotification = (timeline: any) => {
    setSelectedTimelineForNotification(timeline);
    setIsTimelineNotificationModalOpen(true);
  };

  const handleOpenPriorityModal = (publication: Publication) => {
    setSelectedPublicationForPriority(publication);
    setIsPriorityModalOpen(true);
  };

  const handlePriorityUpdated = () => {
    loadData(); // Reload data to show updated priority
  };

  const handleOpenChangeLeadAuthorModal = (publication: Publication) => {
    setSelectedPublicationForLeadAuthor(publication);
    setIsChangeLeadAuthorModalOpen(true);
  };

  const handleLeadAuthorChanged = () => {
    loadData(); // Reload data to show updated lead author
  };

  // Team management functions
  const handleCreateTeam = () => {
    setSelectedTeam(null);
    setIsTeamModalOpen(true);
  };

  const handleEditTeam = (team: Team) => {
    setSelectedTeam(team);
    setIsTeamModalOpen(true);
  };

  const handleDeleteTeam = async (teamId: number) => {
    if (!window.confirm('Are you sure you want to delete this team? This will also remove all team members.')) {
      return;
    }

    try {
      await teamAPI.deleteTeam(teamId);
      toast.success('Team deleted successfully');
      loadData();
    } catch (error: any) {
      console.error('Error deleting team:', error);
      toast.error(error.response?.data?.error || 'Failed to delete team');
    }
  };

  const handleManageTeamMembers = (team: Team) => {
    setSelectedTeam(team);
    setIsTeamMemberModalOpen(true);
  };

  const handleTeamUpdated = () => {
    loadData();
  };

  const handleEditTimeline = (timeline: any) => {
    setSelectedTimelineForEdit(timeline);
    setIsTimelineEditModalOpen(true);
  };

  const handleTimelineUpdated = () => {
    loadData(); // Reload data to show updated timeline
    // Also reload timeline data specifically if we're on the timeline tab
    if (activeTab === 'timeline') {
      forceReloadTimelines();
    }
  };

  // Pagination helper functions
  const ITEMS_PER_PAGE = 15; // For meetings, objectives, timeline, data sources, users
  const PUBLICATIONS_PER_PAGE = 20; // For publications tab

  const getPaginatedData = (data: any[], page: number, itemsPerPage: number = ITEMS_PER_PAGE) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalItems: number, itemsPerPage: number = ITEMS_PER_PAGE) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const toggleMeetingExpanded = (key: string) => {
    const newExpanded = new Set(meetingExpandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setMeetingExpandedItems(newExpanded);
  };

  const toggleObjectiveExpanded = (key: string) => {
    const newExpanded = new Set(objectiveExpandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setObjectiveExpandedItems(newExpanded);
  };

  // Pagination component
  const PaginationComponent = ({ 
    currentPage, 
    totalPages, 
    totalItems, 
    onPageChange, 
    itemsPerPage 
  }: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    itemsPerPage: number;
  }) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {startItem} to {endItem} of {totalItems} items
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    currentPage === pageNum
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1': return 'Ideation';
      case '2': return 'Review & Team';
      case '3': return 'Data Analysis';
      case '4': return 'Manuscript';
      case 'published': return 'Published';
      default: return 'Unknown';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case '1': return 'bg-blue-100 text-blue-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-purple-100 text-purple-800';
      case '4': return 'bg-indigo-100 text-indigo-800';
      case 'published': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Settings className="h-8 w-8 mr-3 text-green-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600 mt-2">Manage publications and user roles</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => {
                  setActiveTab('publications');
                  setPublicationPage(1);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'publications'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="inline h-4 w-4 mr-2" />
                Publications
              </button>
              <button
                onClick={() => {
                  setActiveTab('data-sources');
                  setDataSourcePage(1);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'data-sources'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Database className="inline h-4 w-4 mr-2" />
                Data Sources
              </button>
              <button
                onClick={() => {
                  setActiveTab('meetings');
                  if (meetings.length === 0) {
                    loadAllMeetings();
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meetings'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calendar className="inline h-4 w-4 mr-2" />
                Meeting History
              </button>
              <button
                onClick={() => {
                  setActiveTab('timeline');
                  setTimelinePage(1);
                  if (timelines.length === 0) {
                    loadAllTimelines();
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'timeline'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Clock className="inline h-4 w-4 mr-2" />
                Timeline
              </button>
              <button
                onClick={() => {
                  setActiveTab('objectives');
                  if (objectives.length === 0) {
                    loadAllObjectives();
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'objectives'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Target className="inline h-4 w-4 mr-2" />
                Objectives
              </button>
              <button
                onClick={() => setActiveTab('teams')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'teams'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building2 className="inline h-4 w-4 mr-2" />
                Teams
              </button>
              <button
                onClick={() => setActiveTab('backups')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'backups'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Database className="inline h-4 w-4 mr-2" />
                Backups
              </button>
              {user?.role === 'superadmin' && (
                <button
                  onClick={() => {
                    setActiveTab('users');
                    setUserPage(1);
                  }}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'users'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Users className="inline h-4 w-4 mr-2" />
                  Users
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'publications' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Publication Management</h2>
                
                {publications.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No publications found</h3>
                    <p className="text-gray-600">Publications will appear here when submitted by users.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {getPaginatedData(publications, publicationPage, PUBLICATIONS_PER_PAGE).map((publication) => (
                      <div key={publication.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {publication.title}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>Lead Author: {publication.leadAuthorName || publication.lead_author_name || 'Unknown Author'}</span>
                              <span>•</span>
                              <span>Submitted: {(() => {
                                const date = publication.submittedAt || publication.submitted_at;
                                return date ? new Date(date).toLocaleDateString() : 'Unknown Date';
                              })()}</span>
                              <span>•</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(publication.stage)}`}>
                                {getStageText(publication.stage)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditPublication(publication)}
                            className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors"
                            title="Edit publication"
                          >
                            <Edit3 className="h-4 w-4" />
                            <span className="text-sm">Edit</span>
                          </button>
                        </div>
                        
                        <p className="text-gray-700 mb-4 line-clamp-2">
                          {publication.abstract}
                        </p>

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          {publication.stage === '1' && (
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleApprovePublication(publication.id)}
                                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <CheckCircle className="h-4 w-4" />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => handleRejectPublication(publication.id)}
                                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                              >
                                <XCircle className="h-4 w-4" />
                                <span>Reject</span>
                              </button>
                              <button
                                onClick={() => handleResubmitPublication(publication)}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Edit3 className="h-4 w-4" />
                                <span>Resubmit</span>
                              </button>
                            </div>
                          )}

                          {/* Stage changer */}
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700">Change Stage:</label>
                            <select
                              value={publication.stage}
                              onChange={(e) => handleChangeStage(
                                publication.id,
                                e.target.value as '1' | '2' | '3' | '4' | 'published'
                              )}
                              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                            >
                              <option value="1">1 · Ideation</option>
                              <option value="2">2 · Review & Team</option>
                              <option value="3">3 · Data Analysis</option>
                              <option value="4">4 · Manuscript</option>
                              <option value="published">Published</option>
                            </select>
                          </div>

                          {/* Right side buttons: Change Lead Author (smaller) and Set Priority */}
                          <div className="flex items-center space-x-2 ml-auto">
                            {/* Change Lead Author button - smaller size */}
                            <button
                              onClick={() => handleOpenChangeLeadAuthorModal(publication)}
                              className="flex items-center space-x-1 px-2 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                              title="Change lead author"
                            >
                              <UserIcon className="h-3.5 w-3.5" />
                              <span className="text-xs">Change Lead Author</span>
                            </button>

                          {/* Priority button */}
                          <button
                            onClick={() => handleOpenPriorityModal(publication)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                              publication.priorityLevel === 'priority' || publication.priority_level === 'priority'
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title="Set publication priority"
                          >
                            {publication.priorityLevel === 'priority' || publication.priority_level === 'priority' ? (
                              <AlertTriangle className="h-4 w-4" />
                            ) : (
                              <Star className="h-4 w-4" />
                            )}
                            <span className="text-sm">
                              {publication.priorityLevel === 'priority' || publication.priority_level === 'priority' ? 'Priority' : 'Set Priority'}
                            </span>
                          </button>
                          </div>
                        </div>
                      </div>
                      ))}
                    </div>
                    
                    <PaginationComponent
                      currentPage={publicationPage}
                      totalPages={getTotalPages(publications.length, PUBLICATIONS_PER_PAGE)}
                      totalItems={publications.length}
                      onPageChange={setPublicationPage}
                      itemsPerPage={PUBLICATIONS_PER_PAGE}
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === 'users' && user?.role === 'superadmin' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">User Management</h2>
                
                {users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                    <p className="text-gray-600">Users will appear here when they register.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getPaginatedData(users, userPage, ITEMS_PER_PAGE).map((userItem) => (
                          <tr key={userItem.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {userItem.firstName} {userItem.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{userItem.email}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={userItem.role}
                                onChange={(e) => handleUpdateUserRole(userItem.id, e.target.value)}
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="superadmin">Super Admin</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                // Check if user is active (handle both boolean and number types)
                                const isActive: any = userItem.isActive !== undefined ? userItem.isActive : userItem.is_active;
                                const isUserActive = isActive === true || isActive === 1 || isActive === null || isActive === undefined;
                                
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isUserActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {isUserActive ? 'Active' : 'Inactive'}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {userItem.id !== user?.id && (() => {
                                  // Check if user is active (handle both boolean and number types)
                                  const isActive: any = userItem.isActive !== undefined ? userItem.isActive : userItem.is_active;
                                  const isUserActive = isActive === true || isActive === 1 || isActive === null || isActive === undefined;
                                  
                                  return isUserActive ? (
                                    <button
                                      onClick={() => handleToggleUserStatus(userItem.id, true)}
                                      className="flex items-center space-x-1 text-red-600 hover:text-red-700 transition-colors"
                                      title="Deactivate user"
                                    >
                                      <UserX className="h-4 w-4" />
                                      <span className="text-xs">Deactivate</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleToggleUserStatus(userItem.id, false)}
                                      className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors"
                                      title="Activate user"
                                    >
                                      <UserPlus className="h-4 w-4" />
                                      <span className="text-xs">Activate</span>
                                    </button>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <PaginationComponent
                      currentPage={userPage}
                      totalPages={getTotalPages(users.length, ITEMS_PER_PAGE)}
                      totalItems={users.length}
                      onPageChange={setUserPage}
                      itemsPerPage={ITEMS_PER_PAGE}
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === 'data-sources' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Data Sources Management</h2>
                  <button
                    onClick={handleCreateDataSource}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Data Source</span>
                  </button>
                </div>
                
                {dataSources.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No data sources found</h3>
                    <p className="text-gray-600">Create your first data source to get started.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {getPaginatedData(dataSources, dataSourcePage, ITEMS_PER_PAGE).map((dataSource) => (
                      <div key={dataSource.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-medium text-gray-900">{dataSource.name}</h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                dataSource.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {dataSource.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{dataSource.description}</p>
                            <div className="text-xs text-gray-500">
                              Created: {dataSource.created_at ? new Date(dataSource.created_at).toLocaleDateString() : 'Unknown'}
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleEditDataSource(dataSource)}
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
                              title="Edit data source"
                            >
                              <Edit3 className="h-4 w-4" />
                              <span className="text-xs">Edit</span>
                            </button>
                            <button
                              onClick={() => handleDeleteDataSource(dataSource.id)}
                              className="flex items-center space-x-1 text-red-600 hover:text-red-700 transition-colors"
                              title="Delete data source"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="text-xs">Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      ))}
                    </div>
                    
                    <PaginationComponent
                      currentPage={dataSourcePage}
                      totalPages={getTotalPages(dataSources.length, ITEMS_PER_PAGE)}
                      totalItems={dataSources.length}
                      onPageChange={setDataSourcePage}
                      itemsPerPage={ITEMS_PER_PAGE}
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === 'meetings' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Meeting History</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setMeetingViewMode('current')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        meetingViewMode === 'current'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Current
                    </button>
                    <button
                      onClick={() => setMeetingViewMode('archive')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        meetingViewMode === 'archive'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Archive
                    </button>
                  </div>
                </div>
                
                {meetings.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No meetings found</h3>
                    <p className="text-gray-600">Meetings will appear here when created by lead authors.</p>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      const groupedMeetings = getGroupedMeetings();
                      const meetingKeys = Object.keys(groupedMeetings);
                      
                      if (meetingKeys.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Meetings</h3>
                            <p className="text-gray-600">
                              {meetingViewMode === 'current' 
                                ? 'No meetings found for current publications.'
                                : 'No meetings found for archived publications.'
                              }
                            </p>
                          </div>
                        );
                      }
                      
                      const paginatedKeys = getPaginatedData(meetingKeys, meetingPage, ITEMS_PER_PAGE);
                      const totalPages = getTotalPages(meetingKeys.length, ITEMS_PER_PAGE);
                      
                      return (
                        <>
                          <div className="space-y-3">
                            {paginatedKeys.map(key => {
                              const [publicationId, publicationTitle] = key.split('_');
                              const publicationMeetings = groupedMeetings[key];
                              const firstMeeting = publicationMeetings[0];
                              const isExpanded = meetingExpandedItems.has(key);
                              const hasFiles = publicationMeetings.some((m: any) => m.files && m.files.length > 0);
                              const totalFiles = publicationMeetings.reduce((sum: number, m: any) => sum + (m.files ? m.files.length : 0), 0);
                              
                              return (
                                <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-200">
                                  <div 
                                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleMeetingExpanded(key)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        {isExpanded ? (
                                          <ChevronDown className="h-4 w-4 text-gray-500" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4 text-gray-500" />
                                        )}
                                        <div>
                                          <h3 className="text-lg font-semibold text-gray-900">
                                            {publicationTitle}
                                          </h3>
                                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                                            <span>ID: {publicationId}</span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(firstMeeting.publication_stage)}`}>
                                              {getStageText(firstMeeting.publication_stage)}
                                            </span>
                                            <span className="text-blue-600">
                                              {publicationMeetings.length} meeting{publicationMeetings.length !== 1 ? 's' : ''}
                                            </span>
                                            {hasFiles && (
                                              <span className="text-green-600">
                                                {totalFiles} file{totalFiles !== 1 ? 's' : ''}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {isExpanded && (
                                    <div className="px-4 pb-4 border-t border-gray-100">
                                      <div className="space-y-3 mt-4">
                                        {publicationMeetings.map((meeting: any, index: number) => (
                                          <div key={meeting.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                  <h4 className="font-medium text-gray-900">
                                                    Meeting #{meeting.sequence_number}
                                                  </h4>
                                                  <span className="text-sm text-gray-500">
                                                    {meeting.meeting_date}
                                                  </span>
                                                </div>
                                                
                                                {meeting.meeting_notes && (
                                                  <p className="text-gray-700 text-sm mb-2">{meeting.meeting_notes}</p>
                                                )}
                                                
                                                {meeting.files && meeting.files.length > 0 && (
                                                  <div className="mb-2">
                                                    <p className="text-xs text-gray-500 mb-1">Files:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                      {meeting.files.map((file: any, idx: number) => (
                                                        <button
                                                          key={idx}
                                                          onClick={() => handlePreviewFile(meeting.id, file)}
                                                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors"
                                                        >
                                                          {file.original_filename}
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                  <span>
                                                    Created by: {meeting.first_name} {meeting.last_name}
                                                  </span>
                                                  <span>
                                                    Created: {new Date(meeting.created_at).toLocaleDateString()}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          <PaginationComponent
                            currentPage={meetingPage}
                            totalPages={totalPages}
                            totalItems={meetingKeys.length}
                            onPageChange={setMeetingPage}
                            itemsPerPage={ITEMS_PER_PAGE}
                          />
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'timeline' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Timeline</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setTimelineViewMode('current')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        timelineViewMode === 'current'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Current
                    </button>
                    <button
                      onClick={() => setTimelineViewMode('archive')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        timelineViewMode === 'archive'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Archive
                    </button>
                    {(user?.role === 'admin' || user?.role === 'superadmin') && (
                      <button
                        onClick={() => {
                          if (timelines.length === 0) {
                            loadAllTimelines();
                          }
                          setIsCalendarOpen(true);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors bg-green-600 text-white hover:bg-green-700"
                      >
                        <Calendar className="h-4 w-4" />
                        <span>Calendar</span>
                      </button>
                    )}
                  </div>
                </div>
                
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading timelines...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      const groupedTimelines = getGroupedTimelines();
                      const timelineKeys = Object.keys(groupedTimelines);
                      
                      if (timelineKeys.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Items</h3>
                            <p className="text-gray-600">
                              {timelineViewMode === 'current' 
                                ? 'No timeline items found for current publications.'
                                : 'No timeline items found for archived publications.'
                              }
                            </p>
                          </div>
                        );
                      }
                      
                      const paginatedKeys = getPaginatedData(timelineKeys, timelinePage, ITEMS_PER_PAGE);
                      const totalPages = getTotalPages(timelineKeys.length, ITEMS_PER_PAGE);
                      
                      return (
                        <>
                          {paginatedKeys.map(key => {
                        const [publicationId, publicationTitle] = key.split('_');
                        const publicationTimelines = groupedTimelines[key];
                        const firstTimeline = publicationTimelines[0];
                        
                        return (
                          <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {publicationTitle}
                                </h3>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                  <span>ID: {publicationId}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(firstTimeline.publication_stage)}`}>
                                    {getStageText(firstTimeline.publication_stage)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              {publicationTimelines.map((timeline: any, index: number) => (
                                <div key={timeline.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                          <h4 className="font-medium text-gray-900">{timeline.title}</h4>
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            timeline.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            timeline.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                            timeline.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {timeline.status.replace('_', ' ')}
                                          </span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => handleEditTimeline(timeline)}
                                            className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                                            title="Edit timeline item"
                                          >
                                            <Edit3 className="h-3 w-3" />
                                            <span>Edit</span>
                                          </button>
                                          <button
                                            onClick={() => handleOpenTimelineNotification(timeline)}
                                            className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                                            title="Send email notification to first author"
                                          >
                                            <Mail className="h-3 w-3" />
                                            <span>Notify</span>
                                          </button>
                                        </div>
                                      </div>
                                      
                                      {timeline.description && (
                                        <p className="text-gray-700 text-sm mb-2">{timeline.description}</p>
                                      )}
                                      
                                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                                        {timeline.due_date && (
                                          <span>
                                            Due: {new Date(timeline.due_date).toLocaleDateString()}
                                          </span>
                                        )}
                                        {timeline.completed_date && (
                                          <span>
                                            Completed: {new Date(timeline.completed_date).toLocaleDateString()}
                                          </span>
                                        )}
                                        <span>
                                          Created by: {timeline.created_by_name}
                                        </span>
                                        <span>
                                          Created: {new Date(timeline.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      
                      <PaginationComponent
                        currentPage={timelinePage}
                        totalPages={totalPages}
                        totalItems={timelineKeys.length}
                        onPageChange={setTimelinePage}
                        itemsPerPage={ITEMS_PER_PAGE}
                      />
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'objectives' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Objectives</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setObjectiveViewMode('current')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        objectiveViewMode === 'current'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Current
                    </button>
                    <button
                      onClick={() => setObjectiveViewMode('archive')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        objectiveViewMode === 'archive'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Archive
                    </button>
                  </div>
                </div>
                
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading objectives...</p>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      const groupedObjectives = getGroupedObjectives();
                      const objectiveKeys = Object.keys(groupedObjectives);
                      
                      if (objectiveKeys.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Objectives</h3>
                            <p className="text-gray-600">
                              {objectiveViewMode === 'current' 
                                ? 'No objectives found for current publications.'
                                : 'No objectives found for archived publications.'
                              }
                            </p>
                          </div>
                        );
                      }
                      
                      const paginatedKeys = getPaginatedData(objectiveKeys, objectivePage, ITEMS_PER_PAGE);
                      const totalPages = getTotalPages(objectiveKeys.length, ITEMS_PER_PAGE);
                      
                      return (
                        <>
                          <div className="space-y-3">
                            {paginatedKeys.map(key => {
                              const [publicationId, publicationTitle] = key.split('_');
                              const publicationObjectives = groupedObjectives[key];
                              const firstObjective = publicationObjectives[0];
                              const isExpanded = objectiveExpandedItems.has(key);
                              const hasAttachments = publicationObjectives.some((o: any) => o.attachments && o.attachments.length > 0);
                              const totalAttachments = publicationObjectives.reduce((sum: number, o: any) => sum + (o.attachments ? o.attachments.length : 0), 0);
                              
                              return (
                                <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-200">
                                  <div 
                                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleObjectiveExpanded(key)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        {isExpanded ? (
                                          <ChevronDown className="h-4 w-4 text-gray-500" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4 text-gray-500" />
                                        )}
                                        <div>
                                          <h3 className="text-lg font-semibold text-gray-900">
                                            {publicationTitle}
                                          </h3>
                                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                                            <span>ID: {publicationId}</span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(firstObjective.publication_stage)}`}>
                                              {getStageText(firstObjective.publication_stage)}
                                            </span>
                                            <span className="text-blue-600">
                                              {publicationObjectives.length} objective{publicationObjectives.length !== 1 ? 's' : ''}
                                            </span>
                                            {hasAttachments && (
                                              <span className="text-green-600">
                                                {totalAttachments} attachment{totalAttachments !== 1 ? 's' : ''}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {isExpanded && (
                                    <div className="px-4 pb-4 border-t border-gray-100">
                                      <div className="space-y-3 mt-4">
                                        {publicationObjectives.map((objective: any, index: number) => (
                                          <div key={objective.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                  <h4 className="font-medium text-gray-900">{objective.title}</h4>
                                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    objective.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    objective.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                  }`}>
                                                    {objective.status.replace('_', ' ')}
                                                  </span>
                                                </div>
                                                
                                                {objective.description && (
                                                  <p className="text-gray-700 text-sm mb-2">{objective.description}</p>
                                                )}
                                                
                                                {objective.attachments && objective.attachments.length > 0 && (
                                                  <div className="mb-2">
                                                    <p className="text-xs text-gray-500 mb-1">Attachments:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                      {objective.attachments.map((attachment: any, idx: number) => (
                                                        <button
                                                          key={idx}
                                                          onClick={() => handleDownloadObjectiveAttachment(objective.id, attachment.id, attachment.original_filename)}
                                                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors"
                                                          title="Download attachment"
                                                        >
                                                          {attachment.original_filename} ({Math.round(attachment.file_size / 1024)}KB)
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                  <span>
                                                    Created by: {objective.created_by_name}
                                                  </span>
                                                  <span>
                                                    Created: {new Date(objective.created_at).toLocaleDateString()}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          <PaginationComponent
                            currentPage={objectivePage}
                            totalPages={totalPages}
                            totalItems={objectiveKeys.length}
                            onPageChange={setObjectivePage}
                            itemsPerPage={ITEMS_PER_PAGE}
                          />
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'teams' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Research Teams</h2>
                  <button
                    onClick={handleCreateTeam}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Team</span>
                  </button>
                </div>

                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading teams...</p>
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Teams Found</h3>
                    <p className="text-gray-600 mb-6">
                      Create your first research team to get started.
                    </p>
                    <button
                      onClick={handleCreateTeam}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Team</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {teams.map((team) => (
                      <div key={team.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900 text-sm">
                                {team.full_name}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {team.member_count || 0} members
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEditTeam(team)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit team"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTeam(team.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete team"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        {team.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {team.description}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            Created by {team.created_by_name}
                          </div>
                          <button
                            onClick={() => handleManageTeamMembers(team)}
                            className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 transition-colors"
                          >
                            <Users className="h-3 w-3" />
                            <span>Manage Members</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'backups' && (
              <div>
                <div className="text-center py-12">
                  <Database className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Backup Management</h3>
                  <p className="text-gray-600 mb-6">
                    Access the full backup management interface to create, monitor, and manage database backups.
                  </p>
                  <a
                    href="/admin/backups"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    Open Backup Management
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <EditPublicationModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        publication={selectedPublication}
        onUpdate={loadData}
      />

      <DataSourceModal
        isOpen={isCreateDataSourceModalOpen}
        onClose={handleCloseDataSourceModals}
        onSuccess={loadData}
      />

      <DataSourceModal
        isOpen={isEditDataSourceModalOpen}
        onClose={handleCloseDataSourceModals}
        dataSource={selectedDataSource}
        onSuccess={loadData}
      />

      <FilePreviewModal
        isOpen={isFilePreviewModalOpen}
        onClose={() => setIsFilePreviewModalOpen(false)}
        meetingId={selectedMeetingId || 0}
        file={selectedFile}
      />

      <ResubmitModal
        isOpen={isResubmitModalOpen}
        onClose={() => {
          setIsResubmitModalOpen(false);
          setPublicationToResubmit(null);
        }}
        onConfirm={handleConfirmResubmit}
        publicationTitle={publicationToResubmit?.title || ''}
        isLoading={isResubmitting}
      />

      <TimelineNotificationModal
        isOpen={isTimelineNotificationModalOpen}
        onClose={() => {
          setIsTimelineNotificationModalOpen(false);
          setSelectedTimelineForNotification(null);
        }}
        timeline={selectedTimelineForNotification}
      />

      <TimelineEditModal
        isOpen={isTimelineEditModalOpen}
        onClose={() => {
          setIsTimelineEditModalOpen(false);
          setSelectedTimelineForEdit(null);
        }}
        timeline={selectedTimelineForEdit || { id: 0, title: '', status: 'pending' }}
        onTimelineUpdated={handleTimelineUpdated}
      />

      <PriorityModal
        isOpen={isPriorityModalOpen}
        onClose={() => {
          setIsPriorityModalOpen(false);
          setSelectedPublicationForPriority(null);
        }}
        publication={selectedPublicationForPriority || { id: 0, title: '' }}
        onPriorityUpdated={handlePriorityUpdated}
      />

      <TeamModal
        isOpen={isTeamModalOpen}
        onClose={() => {
          setIsTeamModalOpen(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onTeamUpdated={handleTeamUpdated}
      />

      <TeamMemberModal
        isOpen={isTeamMemberModalOpen}
        onClose={() => {
          setIsTeamMemberModalOpen(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam || { id: 0, institution: '', lab_name: '', team_name: '', full_name: '', created_at: '', updated_at: '', created_by: 0 }}
        onTeamUpdated={handleTeamUpdated}
      />

      <ChangeLeadAuthorModal
        isOpen={isChangeLeadAuthorModalOpen}
        onClose={() => {
          setIsChangeLeadAuthorModalOpen(false);
          setSelectedPublicationForLeadAuthor(null);
        }}
        publication={selectedPublicationForLeadAuthor}
        onSuccess={handleLeadAuthorChanged}
      />

      {isCalendarOpen && (
        <TimelineCalendar
          timelines={timelines}
          publications={publications}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
    </div>
    </>
  );
};

export default AdminPanel;
